// src/features/tickets/components/RichTextEmailEditor.tsx
//
// Wiederverwendbarer Rich-Text-Editor für ausgehende Ticket-Mails (Antworten
// und neue Tickets). Kapselt den Quill-Editor (volle, Gmail-vergleichbare
// Formatierungsleiste) sowie die Aufbereitung der Editor-Ausgabe in
// mailsicheres HTML.
//
// Genutzt von TicketReplyComposer und NewTicketModal, damit beide Composer
// identische Formatierung und HTML-Aufbereitung verwenden.
//
// Exporte:
//   - RichTextEmailEditor: die Editor-Komponente (value/onChange als HTML)
//   - prepareEmailHtml(roh): liefert mailsicheres { bodyHtml, bodyText }
//   - isEmailHtmlEmpty(roh): true, wenn der Editor (ohne Formatierung) leer ist

import React, { useRef, useMemo, useEffect, useCallback } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import DOMPurify from 'dompurify';
import { ProofreadButton } from './ProofreadButton';
import { TicketAttachmentInput } from '../types';
import type { ProofreadMode, ProofreadContext } from '../services/TicketProofreadService';

// Erlaubte Inline-Bild-Formate + Größenlimit pro Bild. Das Gmail-API-Limit
// (35 MB gesamt) wird zusätzlich serverseitig in gmailSender.js geprüft.
const ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];
const MAX_IMAGE_BYTES = 10 * 1024 * 1024;

// Volle Formatierungsleiste (Gmail-vergleichbar). Quill gibt Farbe und
// Hervorhebung bereits inline aus; Größe, Schriftart, Ausrichtung und Einzug
// erzeugt Quill klassenbasiert (ql-size-/ql-font-/ql-align-/ql-indent-) und
// werden vor Versand in Inline-Styles umgewandelt (siehe
// normalizeQuillHtmlForEmail), damit die Formatierung in Mailclients erhalten
// bleibt. Größe und Schriftart nutzen Quills eingebaute Dropdowns mit
// fertigen Labels.
const QUILL_TOOLBAR_CONTAINER = [
  [{ font: [] }],
  [{ size: ['small', false, 'large', 'huge'] }],
  ['bold', 'italic', 'underline', 'strike'],
  [{ color: [] }, { background: [] }],
  [{ align: [] }],
  [{ list: 'ordered' }, { list: 'bullet' }],
  [{ indent: '-1' }, { indent: '+1' }],
  ['blockquote'],
  ['link', 'image'],
  ['clean']
];

const QUILL_FORMATS = [
  'font', 'size',
  'bold', 'italic', 'underline', 'strike',
  'color', 'background',
  'align', 'list', 'indent',
  'blockquote', 'link', 'image'
];

// Scoped Styling für den Editor (Höhe und Rahmen passend zum Composer).
const QUILL_STYLE = `
.xtax-quill .ql-toolbar {
  border-top-left-radius: 6px;
  border-top-right-radius: 6px;
  border-color: #D1D5DB;
  background-color: white;
}
.xtax-quill .ql-container {
  border-bottom-left-radius: 6px;
  border-bottom-right-radius: 6px;
  border-color: #D1D5DB;
  font-family: inherit;
  font-size: 14px;
}
.xtax-quill .ql-editor {
  min-height: 150px;
  max-height: 360px;
  overflow-y: auto;
  color: #111827;
}
.xtax-quill .ql-editor.ql-blank::before {
  color: #9CA3AF;
  font-style: normal;
}
.xtax-quill .ql-editor img {
  max-width: 100%;
  height: auto;
}
`;

interface RichTextEmailEditorProps {
  /** Aktueller HTML-Inhalt (roh aus dem Editor). */
  value: string;
  /** Wird bei jeder Änderung mit dem rohen Editor-HTML aufgerufen. */
  onChange: (html: string) => void;
  placeholder?: string;
  /**
   * Prüfmodus für den ProofreadButton: 'full' (Kundenmail: Korrektur, Ton,
   * Schriftbild, Hinweise) oder 'note' (interne Notiz: nur Rechtschreibung).
   * Default 'full'.
   */
  proofreadMode?: ProofreadMode;
  /**
   * Liefert (nur im 'full'-Modus) den Prüf-Kontext (Verlauf, Anfragenden-/
   * Absendername, Anhänge). Wird zum Prüfzeitpunkt aufgerufen.
   */
  getProofreadContext?: () => Promise<ProofreadContext>;
  /**
   * Meldet, ob aktuell ein nicht übernommener KI-Vorschlag offen ist. Der
   * Composer nutzt das für eine Rückfrage vor dem Senden. Wird unverändert an
   * den ProofreadButton durchgereicht.
   */
  onProofreadPendingChange?: (pending: boolean) => void;
}

export const RichTextEmailEditor: React.FC<RichTextEmailEditorProps> = ({
  value,
  onChange,
  placeholder,
  proofreadMode = 'full',
  getProofreadContext,
  onProofreadPendingChange
}) => {
  const quillRef = useRef<any>(null);

  // Fügt eine Bilddatei als base64-Inline-Bild an der Cursorposition ein.
  // Validiert Format und Größe. Die Umwandlung in echte cid:-Inline-Teile
  // passiert erst beim Versand (prepareEmailHtml + Backend).
  const insertImageFromFile = useCallback((file: File) => {
    if (!file) return;
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      alert('Nicht unterstütztes Bildformat. Erlaubt sind PNG, JPEG, GIF und WebP.');
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      alert('Das Bild ist zu groß (maximal 10 MB pro Bild).');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const quill: any = quillRef.current?.getEditor();
        if (!quill) return;
        const dataUrl = reader.result as string;
        const range = quill.getSelection(true);
        const index = range ? range.index : quill.getLength();
        quill.insertEmbed(index, 'image', dataUrl, 'user');
        quill.setSelection(index + 1, 0);
      } catch (err) {
        console.error('[RichTextEmailEditor] Bild einfügen fehlgeschlagen:', err);
      }
    };
    reader.readAsDataURL(file);
  }, []);

  // Toolbar-Bild-Button: Datei-Dialog öffnen.
  const openImageDialog = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = ALLOWED_IMAGE_TYPES.join(',');
    input.onchange = () => {
      const file = input.files && input.files[0];
      if (file) insertImageFromFile(file);
    };
    input.click();
  }, [insertImageFromFile]);

  const modules = useMemo(() => ({
    toolbar: {
      container: QUILL_TOOLBAR_CONTAINER,
      handlers: { image: openImageDialog }
    },
    // Quill-eigenes Bild-Upload-Modul (Drop/Paste) abschalten: es fügt Bilder
    // zusätzlich zu unseren eigenen onDrop/onPaste-Handlern ein und erzeugt so
    // die Doppelung. Der Toolbar-Bild-Button läuft über handlers.image und
    // bleibt davon unberührt; Drop und Paste laufen weiterhin ausschließlich
    // über unsere eigenen Handler inkl. Format-/Größenprüfung.
    uploader: false
  }), [openImageDialog]);

  // Einfügen per Zwischenablage (Screenshot) und Drag & Drop. Rohe Bild-Dateien
  // behandelt Quill nicht zuverlässig selbst, daher eigene Handler — nur für
  // echte Bild-Dateien; HTML-/Text-Paste läuft unverändert über Quill.
  useEffect(() => {
    const quill: any = quillRef.current?.getEditor();
    if (!quill || !quill.root) return;
    const root: HTMLElement = quill.root;

    const onPaste = (e: ClipboardEvent) => {
      try {
        const items = e.clipboardData?.items;
        if (!items) return;
        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          if (item.kind === 'file' && item.type.startsWith('image/')) {
            const file = item.getAsFile();
            if (file) {
              e.preventDefault();
              insertImageFromFile(file);
            }
          }
        }
      } catch (err) {
        console.error('[RichTextEmailEditor] Einfügen aus Zwischenablage fehlgeschlagen:', err);
      }
    };

    const onDrop = (e: DragEvent) => {
      try {
        const files = e.dataTransfer?.files;
        if (!files || files.length === 0) return;
        const images = Array.from(files).filter((f) => f.type.startsWith('image/'));
        if (images.length === 0) return;
        e.preventDefault();
        images.forEach((f) => insertImageFromFile(f));
      } catch (err) {
        console.error('[RichTextEmailEditor] Drag-and-Drop fehlgeschlagen:', err);
      }
    };

    root.addEventListener('paste', onPaste);
    root.addEventListener('drop', onDrop);
    return () => {
      root.removeEventListener('paste', onPaste);
      root.removeEventListener('drop', onDrop);
    };
  }, [insertImageFromFile]);

  return (
    <>
      <style>{QUILL_STYLE}</style>
      <div className="xtax-quill">
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={value}
          onChange={(html) => onChange(html)}
          modules={modules}
          formats={QUILL_FORMATS}
          placeholder={placeholder}
        />
      </div>
      <ProofreadButton
        quillRef={quillRef}
        mode={proofreadMode}
        getProofreadContext={getProofreadContext}
        onPendingChange={onProofreadPendingChange}
      />
    </>
  );
};

// ============================================
// HTML-Aufbereitung
// ============================================

/**
 * Wandelt geschützte Leerzeichen (U+00A0), die Quill bei der Serialisierung der
 * Editor-Ausgabe zwischen die Wörter setzt, in gewöhnliche Leerzeichen zurück —
 * ausschließlich in Textknoten (Attribute/Styles bleiben unberührt).
 *
 * Hintergrund: Ein geschütztes Leerzeichen verhindert den Zeilenumbruch an
 * dieser Stelle. Stehen sie zwischen allen Wörtern, kann der Browser in der
 * schmalen Ticket-Ansicht nicht an Wortgrenzen umbrechen und bricht stattdessen
 * hart mitten im Wort um — das sieht aus wie eine fehlerhaft versendete
 * Nachricht. Da diese Aufbereitung nur für die eigene Composer-Ausgabe läuft,
 * sind eingehende Kundennachrichten davon nicht betroffen.
 */
function collapseNonBreakingSpacesInText(doc: Document): void {
  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
  const textNodes: Text[] = [];
  let current = walker.nextNode();
  while (current) {
    textNodes.push(current as Text);
    current = walker.nextNode();
  }
  textNodes.forEach((textNode) => {
    const data = textNode.nodeValue;
    if (data && data.indexOf('\u00A0') !== -1) {
      textNode.nodeValue = data.replace(/\u00A0/g, ' ');
    }
  });
}

/**
 * Wandelt die Quill-Editor-Ausgabe in mailsicheres HTML:
 *  - entfernt Quill-UI-Artefakte (span.ql-ui)
 *  - wandelt Quill-Klassen (Größe, Schriftart, Ausrichtung, Einzug) in
 *    Inline-Styles um, damit die Formatierung in Mailclients erhalten bleibt
 *  - wandelt Quills Listen (Container mit data-list je <li>) in echte
 *    <ul>/<ol>-Blöcke um, damit Aufzählungen in Mailclients als Aufzählung
 *    (und nicht nummeriert) erscheinen
 *  - normalisiert geschützte Leerzeichen zwischen Wörtern zurück zu
 *    gewöhnlichen Leerzeichen, damit Mailclients/Ticket-Ansicht sauber an
 *    Wortgrenzen umbrechen
 */
function normalizeQuillHtmlForEmail(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');

  doc.querySelectorAll('span.ql-ui').forEach((el) => el.remove());

  // Quill-Klassen in Inline-Styles übersetzen (Mailclients ignorieren die
  // klassenbasierten Quill-Styles). Mehrere Styles am selben Element werden
  // mit vorhandenen Inline-Styles zusammengeführt.
  doc.querySelectorAll('[class]').forEach((el) => {
    const styles: string[] = [];
    el.className.split(/\s+/).forEach((cls) => {
      if (cls === 'ql-size-small') styles.push('font-size: 0.75em');
      else if (cls === 'ql-size-large') styles.push('font-size: 1.5em');
      else if (cls === 'ql-size-huge') styles.push('font-size: 2.5em');
      else if (cls === 'ql-font-serif') styles.push("font-family: Georgia, 'Times New Roman', serif");
      else if (cls === 'ql-font-monospace') styles.push("font-family: Monaco, 'Courier New', monospace");
      else if (cls === 'ql-align-center') styles.push('text-align: center');
      else if (cls === 'ql-align-right') styles.push('text-align: right');
      else if (cls === 'ql-align-justify') styles.push('text-align: justify');
      else {
        const indent = cls.match(/^ql-indent-(\d+)$/);
        if (indent) styles.push(`margin-left: ${parseInt(indent[1], 10) * 2}em`);
      }
    });
    el.removeAttribute('class');
    if (styles.length > 0) {
      const prev = el.getAttribute('style');
      const prevClean = prev ? prev.trim().replace(/;\s*$/, '') : '';
      el.setAttribute('style', [prevClean, ...styles].filter(Boolean).join('; ') + ';');
    }
  });

  doc.querySelectorAll('ol, ul').forEach((listEl) => {
    const original: 'ul' | 'ol' = listEl.tagName === 'UL' ? 'ul' : 'ol';
    const items = Array.from(listEl.children).filter(
      (c): c is HTMLLIElement => c.tagName === 'LI'
    );
    if (items.length === 0) return;

    const fragment = doc.createDocumentFragment();
    let group: HTMLElement | null = null;
    let groupType: 'ul' | 'ol' | null = null;

    items.forEach((li) => {
      const dataList = li.getAttribute('data-list');
      const type: 'ul' | 'ol' =
        dataList === 'bullet' ? 'ul' : dataList === 'ordered' ? 'ol' : original;
      if (type !== groupType) {
        group = doc.createElement(type);
        fragment.appendChild(group);
        groupType = type;
      }
      li.removeAttribute('data-list');
      if (group) group.appendChild(li);
    });

    listEl.replaceWith(fragment);
  });

  collapseNonBreakingSpacesInText(doc);

  return doc.body.innerHTML;
}

/**
 * DOMPurify mit enger Allowlist der mailsicheren Tags. Defense-in-Depth
 * zusätzlich zur Sanitisierung beim Rendern im Thread.
 */
function sanitizeComposerHtml(html: string): string {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['p', 'br', 'span', 'strong', 'em', 'u', 's', 'ol', 'ul', 'li', 'a', 'blockquote', 'img'],
    // src lässt cid: (Inline-Bild) sowie data:/https zu (DOMPurify-Default-URI-Filter
    // erlaubt cid und http(s)). Inline-Bilder sind zum Sanitize-Zeitpunkt bereits
    // auf cid: umgeschrieben.
    ALLOWED_ATTR: ['href', 'target', 'rel', 'style', 'src', 'alt', 'width', 'height']
  });
}

/**
 * Plain-Text-Fassung des HTML für body_text (Text-Alternative der Mail,
 * Listen-Vorschauen, Leer-Prüfung). Block-Elemente und <br> werden zu
 * Zeilenumbrüchen.
 */
function htmlToPlainText(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc.querySelectorAll('br').forEach((br) => br.replaceWith('\n'));
  doc.querySelectorAll('p, div, li, blockquote, h1, h2, h3, h4, h5, h6').forEach((el) => {
    el.appendChild(doc.createTextNode('\n'));
  });
  const text = doc.body.textContent || '';
  return text.replace(/\n{3,}/g, '\n\n').replace(/[ \t]+\n/g, '\n').trim();
}

/**
 * Bereitet die rohe Editor-Ausgabe für Speicherung und Versand auf:
 * mailsicheres HTML (Inline-Styles, normalisierte Listen) plus eine
 * Plain-Text-Fassung.
 */
/** Dateiendung passend zum Bild-MIME-Typ (für den synthetischen Inline-Dateinamen). */
function extensionForImageType(contentType: string): string {
  switch (contentType) {
    case 'image/png': return 'png';
    case 'image/jpeg': return 'jpg';
    case 'image/gif': return 'gif';
    case 'image/webp': return 'webp';
    default: return 'png';
  }
}

/**
 * Löst eingebettete <img src="data:..."> aus dem HTML heraus: erzeugt je Bild
 * eine eindeutige content_id, ersetzt die Bildquelle durch cid:<content_id> und
 * sammelt die Bilddaten als Inline-Liste (TicketAttachmentInput mit is_inline).
 * Externe http(s)-Bilder bleiben unverändert. Begrenzt zugleich die Breite per
 * Inline-Style, damit große Screenshots Layout und Mail nicht sprengen.
 */
function extractInlineImages(html: string): { html: string; inlineImages: TicketAttachmentInput[] } {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const imgs = Array.from(doc.querySelectorAll('img'));
  const inlineImages: TicketAttachmentInput[] = [];

  imgs.forEach((img, i) => {
    const src = img.getAttribute('src') || '';
    const match = src.match(/^data:([^;,]+)(;base64)?,(.*)$/i);
    if (!match) return;

    const contentType = (match[1] || 'image/png').toLowerCase();
    const isBase64 = !!match[2];
    let base64: string;
    try {
      base64 = isBase64 ? match[3] : btoa(decodeURIComponent(match[3]));
    } catch {
      return;
    }

    const contentId = `xtaximg-${Date.now()}-${i}-${Math.random().toString(36).slice(2, 8)}`;
    inlineImages.push({
      filename: `inline-${contentId}.${extensionForImageType(contentType)}`,
      content_base64: base64,
      content_type: contentType,
      file_size: Math.floor((base64.length * 3) / 4),
      is_inline: true,
      content_id: contentId
    });

    img.setAttribute('src', `cid:${contentId}`);
    const prevStyle = (img.getAttribute('style') || '').trim().replace(/;\s*$/, '');
    const sizing = 'max-width:100%; height:auto';
    img.setAttribute('style', prevStyle ? `${prevStyle}; ${sizing}` : sizing);
    img.removeAttribute('width');
    img.removeAttribute('height');
  });

  return { html: doc.body.innerHTML, inlineImages };
}

/**
 * Bereitet die rohe Editor-Ausgabe für Speicherung und Versand auf:
 * mailsicheres HTML (Inline-Styles, normalisierte Listen, cid:-Inline-Bilder)
 * plus Plain-Text-Fassung und die Liste der Inline-Bilder.
 */
export function prepareEmailHtml(rawHtml: string): { bodyHtml: string; bodyText: string; inlineImages: TicketAttachmentInput[] } {
  const normalized = normalizeQuillHtmlForEmail(rawHtml);
  const { html: withCids, inlineImages } = extractInlineImages(normalized);
  const bodyHtml = sanitizeComposerHtml(withCids);
  const bodyText = htmlToPlainText(bodyHtml);
  return { bodyHtml, bodyText, inlineImages };
}

/** true, wenn der Editor keinen sichtbaren Text enthält. */
export function isEmailHtmlEmpty(rawHtml: string): boolean {
  return !htmlToPlainText(rawHtml).trim();
}
